import { useState } from "react";
import { CalendarPlus, Share2, Link2, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");
const stamp = (d, t) => `${d.replace(/-/g, "")}T${t.replace(":", "")}00`;

const endTime = (c) => {
  const [h, m] = (c.show_time || "20:00").split(":").map(Number);
  return `${pad(Math.min(h + 3, 23))}:${pad(m)}`;
};

const icsFile = (c, url) => {
  const esc = (s) => (s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Fallsview Summer Concert Series//EN", "BEGIN:VEVENT",
    `UID:${c.slug}@fallsview-summer-concerts`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`,
    `DTSTART;TZID=America/Toronto:${stamp(c.date, c.show_time)}`,
    `DTEND;TZID=America/Toronto:${stamp(c.date, endTime(c))}`,
    `SUMMARY:${esc(`${c.artist} — Fallsview Summer Concert Series`)}`,
    `LOCATION:${esc(`${c.venue}, ${c.address}`)}`,
    `DESCRIPTION:${esc(`Doors ${c.doors_time}. ${url}`)}`,
    `URL:${url}`, "END:VEVENT", "END:VCALENDAR",
  ];
  return lines.join("\r\n");
};

export const CalendarShare = ({ concert: c }) => {
  const [copied, setCopied] = useState(false);
  const url = window.location.href;
  const title = `${c.artist} at the Fallsview Summer Concert Series`;
  const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${stamp(c.date, c.show_time)}/${stamp(c.date, endTime(c))}&ctz=America/Toronto&location=${encodeURIComponent(`${c.venue}, ${c.address}`)}&details=${encodeURIComponent(url)}`;
  const text = encodeURIComponent(`${title} — ${url}`);

  const downloadIcs = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([icsFile(c, url)], { type: "text/calendar" }));
    a.download = `${c.slug}.ics`;
    a.click();
  };

  const nativeShare = async () => {
    try { await navigator.share({ title, url }); } catch { /* cancelled */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const social = [
    ["WhatsApp", `https://wa.me/?text=${text}`, "share-whatsapp"],
    ["Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "share-facebook"],
    ["X", `https://twitter.com/intent/tweet?text=${text}`, "share-x"],
  ];

  if (c.is_past) return null;

  return (
    <div data-testid="calendar-share" className="rounded-[24px] border border-white/10 bg-[#12141C] p-6">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">Save the date</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <button data-testid="add-to-calendar-ics" onClick={downloadIcs} className="btn-ghost justify-center"><CalendarPlus size={15} /> Apple / Outlook</button>
        <a data-testid="add-to-calendar-google" href={gcal} target="_blank" rel="noopener noreferrer" className="btn-ghost justify-center"><CalendarPlus size={15} /> Google</a>
      </div>
      <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">Bring your crew</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {typeof navigator !== "undefined" && navigator.share && (
          <button data-testid="share-native" onClick={nativeShare} className="btn-ghost"><Share2 size={15} /> Share</button>
        )}
        {social.map(([label, href, id]) => (
          <a key={id} data-testid={id} href={href} target="_blank" rel="noopener noreferrer" className="btn-ghost" aria-label={`Share on ${label}`}>{label}</a>
        ))}
        <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent(title)}&body=${text}`} className="btn-ghost" aria-label="Share by email"><Mail size={15} /></a>
        <button data-testid="share-copy-link" onClick={copy} className="btn-ghost" aria-label="Copy link">{copied ? <Check size={15} /> : <Link2 size={15} />} {copied ? "Copied" : "Copy"}</button>
      </div>
    </div>
  );
};
